// Montørhold: små grønne varevogne (og et skib), der kører ud til knækkede ledninger.
// Positionen interpoleres mellem to sim-ticks, så de glider i stedet for at hoppe.

import { BitmapText, Container, Graphics, Sprite } from 'pixi.js';
import type { Crew, State } from '../sim';
import { REINFORCE_TICKS } from '../sim/crews';
import { lineById, repairTicks } from '../sim/grid';
import { NUM_FONT } from './fx';
import type { MapObject } from './cities-layer';
import type { Piece } from './declutter';
import type { P } from './geo';
import { C } from './palette';
import type { View } from './view';

interface UnitView {
  s: Sprite;
  ring: Graphics;
  face: number;
  sparkAt: number;
  bob: number;
  /** "×3", når flere hold står samme sted og vises som én bil. */
  badge: BitmapText | null;
  /** Testkrog: hvem holdet hører til (nodens id, eller ledningens), og om det holder stille. */
  owner: string;
  fixed: boolean;
  /** Spor: telefonkort: forskydningen væk fra huse, navne og figurer (declutter.ts), og målet. */
  nudge: P;
  placed?: boolean;
  target: P;
}

export class UnitsLayer {
  readonly c = new Container();
  private views = new Map<string, UnitView>();
  private prev = new Map<string, P>();
  private cur = new Map<string, P>();

  constructor(private onSpark: (w: P) => void) {}

  reset(): void {
    for (const u of this.views.values()) {
      u.s.destroy();
      u.ring.destroy();
      u.badge?.destroy();
    }
    this.views.clear();
    this.prev.clear();
    this.cur.clear();
  }

  /** Kaldes lige før hvert sim-tick, så vi kan glide fra den gamle position. */
  snapshot(state: State): void {
    this.prev.clear();
    for (const c of state.crews) this.prev.set(c.id, { x: c.x, y: c.y });
  }

  update(v: View): void {
    const seen = new Set<string>();
    // Hold, der holder eller arbejder samme sted, vises som én bil med "×N" i stedet for en
    // stribe biler. Hold på vej, der ville stå oven i hinanden, skubbes til side, så hver kan ses.
    const placed: P[] = [];
    const spotOf = (c: Crew): string | null => (c.task === 'driving' ? null : `${c.task === 'idle' ? 'i' : 'w'}${Math.round(c.x)},${Math.round(c.y)}`);
    const count = new Map<string, number>();
    for (const c of v.state.crews) {
      const key = spotOf(c);
      if (key) count.set(key, (count.get(key) ?? 0) + 1);
    }
    const leader = new Map<string, P>();
    for (const crew of v.state.crews) {
      seen.add(crew.id);
      let u = this.views.get(crew.id);
      if (!u) {
        const s = v.atlas.sprite(crew.kind === 'ship' ? 'ship' : 'van');
        const ring = new Graphics();
        this.c.addChild(ring, s);
        u = { s, ring, face: 1, sparkAt: 0, bob: Math.random() * 6, badge: null, owner: '', fixed: false, nudge: { x: 0, y: 0 }, target: { x: 0, y: 0 } };
        this.views.set(crew.id, u);
      }
      const p0 = this.prev.get(crew.id) ?? { x: crew.x, y: crew.y };
      const real = { x: p0.x + (crew.x - p0.x) * v.alpha, y: p0.y + (crew.y - p0.y) * v.alpha };
      const w = v.warp(real);
      let sp = v.cam.toScreen(w);
      if (crew.task === 'idle') sp = { x: sp.x - 14 * v.k, y: sp.y + 18 * v.k };
      const key = spotOf(crew);
      const home = crew.task === 'idle' ? v.state.nodes.find((n) => n.active && Math.abs(n.x - crew.x) < 0.01 && Math.abs(n.y - crew.y) < 0.01) : undefined;
      u.owner = home ? home.id : `line:${crew.lineId || key}`;
      u.fixed = crew.task !== 'driving';
      const lead = key ? leader.get(key) : undefined;
      if (lead) {
        // Står bag den første bil på pladsen: skjult, men tappes samme sted.
        u.s.visible = false;
        u.ring.clear();
        if (u.badge) u.badge.visible = false;
        u.s.position.set(lead.x, lead.y);
        continue;
      }
      u.s.visible = true;
      for (let i = 0; i < 6 && placed.some((q) => Math.hypot(q.x - sp.x, q.y - sp.y) < 20 * v.k); i++) {
        sp = { x: sp.x + 22 * v.k, y: sp.y + 4 * v.k };
      }
      placed.push(sp);
      // Et hold, der holder stille, står ved sin plads, men uden for naboens hus og navn.
      if (crew.task !== 'driving') sp = { x: sp.x + u.nudge.x, y: sp.y + u.nudge.y };
      else u.nudge = u.target = { x: 0, y: 0 };
      if (key) leader.set(key, sp);
      this.drawBadge(u, key ? (count.get(key) ?? 1) : 1, sp, v.k);
      const dx = crew.x - p0.x;
      if (Math.abs(dx) > 0.01) u.face = dx < 0 ? -1 : 1;
      const k = v.k * 0.95;
      const driving = crew.task === 'driving';
      const bounce = driving && !v.calm ? Math.abs(Math.sin(v.now / 90 + u.bob)) * 1.6 * k : 0;
      u.s.position.set(sp.x, sp.y - bounce);
      u.s.scale.set(k * u.face, k);
      u.s.rotation = driving && !v.calm ? Math.sin(v.now / 140 + u.bob) * 0.04 : 0;
      this.drawProgress(u, crew, v, sp);
      if ((crew.task === 'repairing' || crew.task === 'reinforcing') && v.now > u.sparkAt && !v.calm) {
        u.sparkAt = v.now + 420;
        this.onSpark(w);
      }
    }
    for (const [id, u] of this.views) {
      if (seen.has(id)) continue;
      u.s.destroy();
      u.ring.destroy();
      u.badge?.destroy();
      this.views.delete(id);
    }
  }

  private drawBadge(u: UnitView, n: number, sp: P, k: number): void {
    if (n <= 1) {
      if (u.badge) u.badge.visible = false;
      return;
    }
    if (!u.badge) {
      u.badge = new BitmapText({ text: '', style: { fontFamily: NUM_FONT, fontSize: 22 } });
      u.badge.anchor.set(0, 0.5);
      this.c.addChild(u.badge);
    }
    const b = u.badge;
    b.visible = true;
    const t = `×${n}`;
    if (b.text !== t) b.text = t;
    // Over bilens hjørne, hvor bynavnet (til højre for byen) ikke står.
    b.scale.set(k * 0.85);
    b.position.set(sp.x + 4 * k, sp.y - 24 * k);
  }

  private drawProgress(u: UnitView, crew: Crew, v: View, sp: P): void {
    const g = u.ring;
    g.clear();
    g.position.set(0, 0);
    if (crew.task !== 'repairing' && crew.task !== 'reinforcing') return;
    const line = lineById(v.state, crew.lineId);
    const total = crew.task === 'repairing' && line ? repairTicks(line) : REINFORCE_TICKS;
    const left = Math.max(0, crew.ticks - v.alpha);
    const frac = Math.max(0, Math.min(1, 1 - left / Math.max(1, total)));
    const k = v.k;
    const cx = sp.x;
    const cy = sp.y - 30 * k;
    const r = 11 * k;
    g.circle(cx, cy, r + 3 * k).fill({ color: C.white, alpha: 0.95 }).stroke({ width: 1.5 * k, color: C.ink, alpha: 0.4 });
    g.circle(cx, cy, r).stroke({ width: 3.5 * k, color: C.green, alpha: 0.2 });
    if (frac > 0.001) g.moveTo(cx, cy - r).arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2).stroke({ width: 3.5 * k, color: C.green, cap: 'round' });
    // Skruenøgle i midten.
    g.moveTo(cx - 3.5 * k, cy + 3.5 * k).lineTo(cx + 2 * k, cy - 2 * k).stroke({ width: 2.2 * k, color: C.ink, cap: 'round' });
    g.circle(cx + 3 * k, cy - 3 * k, 2.4 * k).stroke({ width: 1.8 * k, color: C.ink });
  }

  /** Holdene, der holder stille (declutter.ts), målt med den forskydning, der er tegnet nu. */
  pieces(): Array<{ piece: Piece; view: { nudge: P; target: P; placed?: boolean }; parts: Container[] }> {
    const out: Array<{ piece: Piece; view: { nudge: P; target: P; placed?: boolean }; parts: Container[] }> = [];
    for (const u of this.views.values()) {
      if (!u.fixed || !u.s.visible) continue;
      const boxes = [];
      for (const o of [u.s, u.badge, u.ring]) {
        if (!o || !o.visible || o.destroyed) continue;
        const b = o.getBounds();
        if (b.width > 0.5 && b.height > 0.5) boxes.push({ x: b.x, y: b.y, w: b.width, h: b.height });
      }
      if (boxes.length) out.push({ piece: { owner: u.owner, boxes, nudge: u.nudge, target: u.target }, view: u, parts: [u.s, u.ring, ...(u.badge ? [u.badge] : [])] });
    }
    return out;
  }

  /** Testkrog (scripts/maptest.mjs): bilerne, deres "×N" og fremdriftsringe på skærmen. */
  objectBoxes(): MapObject[] {
    const out: MapObject[] = [];
    for (const u of this.views.values()) {
      for (const [type, o] of [['crew', u.s], ['crewBadge', u.badge], ['crewRing', u.ring]] as const) {
        if (!o || !o.visible || o.destroyed) continue;
        const b = o.getBounds();
        if (b.width > 0.5 && b.height > 0.5) out.push({ owner: u.owner, type, box: { x: b.x, y: b.y, w: b.width, h: b.height }, fixed: u.fixed });
      }
    }
    return out;
  }

  /** Skærmposition for et hold (til hit-test og tests). */
  screenOf(id: string): P | null {
    const u = this.views.get(id);
    return u ? { x: u.s.x, y: u.s.y } : null;
  }
}
